/**
 * UserEntityMembership + UserHoldingMembership — N:M binding с per-binding role.
 * MembershipResolved — computed view discriminated union (direct vs holding-derived).
 * S-multi-1 BM-1-4.
 */
import { z } from 'zod';
import { HoldingRole, MembershipRole, MembershipStatus } from '../enums.js';
import { IsoDateTime, UuidString } from '../primitives.js';

export const UserEntityMembership = z.object({
  user_id: UuidString,
  entity_id: UuidString,
  role: MembershipRole,
  status: MembershipStatus,
  invited_by: UuidString.nullable(),
  /** B83 пака `tails-closeout` (С-I, NC-53): когда и кем членство отключено; null — активно. */
  disabled_at: IsoDateTime.nullable().optional(),
  disabled_by: UuidString.nullable().optional(),
  created_at: IsoDateTime,
  updated_at: IsoDateTime,
});
export type UserEntityMembership = z.infer<typeof UserEntityMembership>;

export const UserEntityMembershipCreate = z
  .object({
    entity_id: UuidString,
    role: MembershipRole,
    status: MembershipStatus.optional().default('active'),
  })
  // zod-strict-ok: input request-body
  .strict();
export type UserEntityMembershipCreate = z.infer<typeof UserEntityMembershipCreate>;

export const UserEntityMembershipPatch = z
  .object({
    role: MembershipRole.optional(),
    status: MembershipStatus.optional(),
  })
  // zod-strict-ok: input request-body
  .strict();
export type UserEntityMembershipPatch = z.infer<typeof UserEntityMembershipPatch>;

export const UserHoldingMembership = z.object({
  user_id: UuidString,
  holding_id: UuidString,
  role: HoldingRole,
  status: MembershipStatus,
  invited_by: UuidString.nullable(),
  /** B83 пака `tails-closeout` (С-I, NC-53): когда и кем членство отключено; null — активно. */
  disabled_at: IsoDateTime.nullable().optional(),
  disabled_by: UuidString.nullable().optional(),
  created_at: IsoDateTime,
  updated_at: IsoDateTime,
});
export type UserHoldingMembership = z.infer<typeof UserHoldingMembership>;

export const UserHoldingMembershipCreate = z
  .object({
    holding_id: UuidString,
    role: HoldingRole,
    status: MembershipStatus.optional().default('active'),
  })
  // zod-strict-ok: input request-body
  .strict();
export type UserHoldingMembershipCreate = z.infer<typeof UserHoldingMembershipCreate>;

export const UserHoldingMembershipPatch = z
  .object({
    role: HoldingRole.optional(),
    status: MembershipStatus.optional(),
  })
  // zod-strict-ok: input request-body
  .strict();
export type UserHoldingMembershipPatch = z.infer<typeof UserHoldingMembershipPatch>;

/**
 * MembershipResolved — computed access entry per (user, entity).
 * Discriminated union по `source`:
 * - 'direct'  — прямая запись в user_entity_memberships
 * - 'holding' — derived через user_holding_memberships (holding_admin → company_admin etc)
 *
 * Используется в /auth/me + /me/memberships для FE switcher / RBAC hooks.
 */
export const MembershipResolved = z.discriminatedUnion('source', [
  z.object({
    source: z.literal('direct'),
    entity_id: UuidString,
    entity_name: z.string(),
    role: MembershipRole,
  }),
  z.object({
    source: z.literal('holding'),
    entity_id: UuidString,
    entity_name: z.string(),
    holding_id: UuidString,
    holding_name: z.string(),
    /** Effective role after holding→entity mapping (holding_admin → company_admin etc). */
    role: MembershipRole,
  }),
]);
export type MembershipResolved = z.infer<typeof MembershipResolved>;
